import { InputRichText, Tooltip } from '@dixa/lib-shared-habla';
import { useCallback, useContext } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { copilotDropdownFeatureFlags } from 'components/copilotDropdown/copilotDropdown.constants';
import { useQuickAiDraft } from 'components/editorCopilotDropdown/hooks/useQuickAiDraft';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

const getTooltipContent = (hasAgent: boolean, isLoading: boolean): string => {
  if (!hasAgent) {
    return 'No default AI agent set';
  }
  if (isLoading) {
    return 'Generating draft...';
  }
  return 'Quick Mim drafts';
};

export const QuickAiDraft = ({
  csid,
}: PinnedActionProps): JSX.Element | null => {
  const { hasCopilot } = useCopilotTrial();
  const flags = useFeatureFlag(copilotDropdownFeatureFlags);
  const isAvailable = hasCopilot && flags.mimDrafts;
  const { content } = useContext(InputRichText.Context);

  const handleInserted = useCallback(() => {
    content.setUiBlocker(null);
  }, [content]);

  const handleError = useCallback(() => {
    content.setUiBlocker(
      <UIBlockerError
        onClick={() => {
          content.setUiBlocker(null);
        }}
        errorText="Failed to generate draft"
      />,
    );
  }, [content]);

  const { generateDraft, hasAgent, isLoading } = useQuickAiDraft({
    dixaConversationId: csid ?? '',
    onInserted: handleInserted,
    onError: handleError,
    skip: !isAvailable,
  });

  const onClickHandler = useCallback(() => {
    content.setUiBlocker(<UIBlockerLoading loadingText="Generating draft" />);
    generateDraft();
  }, [content, generateDraft]);

  if (!isAvailable) {
    return null;
  }

  return (
    <Tooltip
      content={
        <CommandKeybindingsTooltipContent commandName="richTextEditor:quickAiDraft">
          {getTooltipContent(hasAgent, isLoading)}
        </CommandKeybindingsTooltipContent>
      }
    >
      <InputRichText.ToolbarButton
        icon="mim"
        onClick={onClickHandler}
        disabled={!csid || !hasAgent || isLoading}
      />
    </Tooltip>
  );
};
